import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../config";

// Define the SubPrompt interface
export interface SubPrompt {
  id: string;
  title: string;
  promptContent: string;
  enabled: boolean;
  globalPromptId: string;
  iconType?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

// Define the Prompt interface
export interface GlobalPrompt {
  id: string;
  title: string;
  promptContent: string;
  enabled: boolean;
  isGlobal: boolean;
  iconType?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  subPrompts?: SubPrompt[];
}

// Define the state interface
interface GlobalPromptsState {
  prompts: GlobalPrompt[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: GlobalPromptsState = {
  prompts: [],
  loading: false,
  error: null,
};

// Async thunk for fetching global prompts
export const fetchGlobalPrompts = createAsyncThunk(
  'globalPrompts/fetch',
  async (_, { rejectWithValue }) => {
    try {
      // Get the token and userId from local storage
      const token = localStorage.getItem('clerk-token');
      const userId = localStorage.getItem('clerk-user-id');
      
      if (!token) {
        return rejectWithValue('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/prompts/global/accessible`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId || ''
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch prompts: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch prompts');
    }
  }
);

// Async thunk for creating a subprompt
export const createSubPrompt = createAsyncThunk(
  'globalPrompts/createSubPrompt',
  async ({ globalPromptId, subPromptData }: { globalPromptId: string, subPromptData: Partial<SubPrompt> }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('clerk-token');
      const userId = localStorage.getItem('clerk-user-id');
      
      if (!token) {
        return rejectWithValue('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/prompts/global/${globalPromptId}/subprompts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId || ''
        },
        body: JSON.stringify(subPromptData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create subprompt: ${response.statusText}`);
      }
      
      const data = await response.json();
      return { globalPromptId, subPrompt: data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create subprompt');
    }
  }
);

// Async thunk for updating a subprompt
export const updateSubPrompt = createAsyncThunk(
  'globalPrompts/updateSubPrompt',
  async ({ subPromptId, subPromptData }: { subPromptId: string, subPromptData: Partial<SubPrompt> }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('clerk-token');
      const userId = localStorage.getItem('clerk-user-id');
      
      if (!token) {
        return rejectWithValue('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/prompts/subprompts/${subPromptId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId || ''
        },
        body: JSON.stringify(subPromptData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update subprompt: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update subprompt');
    }
  }
);

// Async thunk for deleting a subprompt
export const deleteSubPrompt = createAsyncThunk(
  'globalPrompts/deleteSubPrompt',
  async ({ subPromptId, globalPromptId }: { subPromptId: string, globalPromptId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('clerk-token');
      const userId = localStorage.getItem('clerk-user-id');
      
      if (!token) {
        return rejectWithValue('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/prompts/subprompts/${subPromptId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId || ''
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete subprompt: ${response.statusText}`);
      }
      
      return { subPromptId, globalPromptId };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete subprompt');
    }
  }
);

// Async thunk for toggling subprompt enabled status
export const toggleSubPromptEnabled = createAsyncThunk(
  'globalPrompts/toggleSubPromptEnabled',
  async ({ subPromptId, enabled }: { subPromptId: string, enabled: boolean }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('clerk-token');
      const userId = localStorage.getItem('clerk-user-id');
      
      if (!token) {
        return rejectWithValue('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/prompts/subprompts/${subPromptId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId || ''
        },
        body: JSON.stringify({ enabled })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to toggle subprompt status: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to toggle subprompt status');
    }
  }
);

// Create the slice
const globalPromptsSlice = createSlice({
  name: 'globalPrompts',
  initialState,
  reducers: {
    setGlobalPrompts: (state, action: PayloadAction<GlobalPrompt[]>) => {
      state.prompts = action.payload;
    },
    addPrompt: (state, action: PayloadAction<GlobalPrompt>) => {
      state.prompts.push(action.payload);
    },
    updatePrompt: (state, action: PayloadAction<GlobalPrompt>) => {
      const index = state.prompts.findIndex(prompt => prompt.id === action.payload.id);
      if (index !== -1) {
        state.prompts[index] = action.payload;
      }
    },
    deletePrompt: (state, action: PayloadAction<string>) => {
      state.prompts = state.prompts.filter(prompt => prompt.id !== action.payload);
    },
    togglePromptEnabled: (state, action: PayloadAction<string>) => {
      const index = state.prompts.findIndex(prompt => prompt.id === action.payload);
      if (index !== -1) {
        state.prompts[index].enabled = !state.prompts[index].enabled;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalPrompts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalPrompts.fulfilled, (state, action) => {
        state.loading = false;
        state.prompts = action.payload;
      })
      .addCase(fetchGlobalPrompts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSubPrompt.fulfilled, (state, action) => {
        const { globalPromptId, subPrompt } = action.payload;
        const promptIndex = state.prompts.findIndex(p => p.id === globalPromptId);
        
        if (promptIndex !== -1) {
          if (!state.prompts[promptIndex].subPrompts) {
            state.prompts[promptIndex].subPrompts = [];
          }
          state.prompts[promptIndex].subPrompts?.push(subPrompt);
        }
      })
      .addCase(updateSubPrompt.fulfilled, (state, action) => {
        const updatedSubPrompt = action.payload;
        const globalPromptId = updatedSubPrompt.globalPromptId;
        const promptIndex = state.prompts.findIndex(p => p.id === globalPromptId);
        
        if (promptIndex !== -1 && state.prompts[promptIndex].subPrompts) {
          const subPromptIndex = state.prompts[promptIndex].subPrompts!.findIndex(
            sp => sp.id === updatedSubPrompt.id
          );
          
          if (subPromptIndex !== -1) {
            state.prompts[promptIndex].subPrompts![subPromptIndex] = updatedSubPrompt;
          }
        }
      })
      .addCase(deleteSubPrompt.fulfilled, (state, action) => {
        const { subPromptId, globalPromptId } = action.payload;
        const promptIndex = state.prompts.findIndex(p => p.id === globalPromptId);
        
        if (promptIndex !== -1 && state.prompts[promptIndex].subPrompts) {
          state.prompts[promptIndex].subPrompts = state.prompts[promptIndex].subPrompts!.filter(
            sp => sp.id !== subPromptId
          );
        }
      })
      .addCase(toggleSubPromptEnabled.fulfilled, (state, action) => {
        const updatedSubPrompt = action.payload;
        const globalPromptId = updatedSubPrompt.globalPromptId;
        const promptIndex = state.prompts.findIndex(p => p.id === globalPromptId);
        
        if (promptIndex !== -1 && state.prompts[promptIndex].subPrompts) {
          const subPromptIndex = state.prompts[promptIndex].subPrompts!.findIndex(
            sp => sp.id === updatedSubPrompt.id
          );
          
          if (subPromptIndex !== -1) {
            state.prompts[promptIndex].subPrompts![subPromptIndex] = updatedSubPrompt;
          }
        }
      });
  },
});

// Export actions
export const { 
  setGlobalPrompts, 
  addPrompt, 
  updatePrompt, 
  deletePrompt, 
  togglePromptEnabled 
} = globalPromptsSlice.actions;

// Export reducer
export default globalPromptsSlice.reducer; 